"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const PLATFORMS = ["Instagram", "LinkedIn", "X (Twitter)", "TikTok", "Facebook"] as const;
const LANGUAGES = ["English", "Spanish", "French", "Portuguese", "German"] as const;

interface StepAiConfigProps {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function StepAiConfig({ brandId, onContinue, onSkip, onBack }: StepAiConfigProps) {
  const { t } = useTranslation(["wizard", "common"]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(["Instagram", "LinkedIn"]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["English"]);
  const [saving, setSaving] = useState(false);

  function toggle(arr: string[], setArr: (v: string[]) => void, val: string) {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  }

  async function handleContinue() {
    setSaving(true);
    try {
      await fetch(`/api/brands/${brandId}/ai-config`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ platforms: selectedPlatforms, languages: selectedLanguages }),
      });
    } finally {
      setSaving(false);
      onContinue();
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[18px] font-bold text-foreground tracking-tight">{t("steps.aiConfig.title", { ns: "wizard" })}</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          {t("steps.aiConfig.description", { ns: "wizard" })}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t("steps.aiConfig.platformsLabel", { ns: "wizard" })}</Label>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => {
            const active = selectedPlatforms.includes(p);
            return (
              <button key={p} type="button" onClick={() => toggle(selectedPlatforms, setSelectedPlatforms, p)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${active ? "bg-brand-subtle border-brand text-brand-subtle-text" : "bg-card border-border text-foreground hover:bg-muted"}`}>
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t("steps.aiConfig.languagesLabel", { ns: "wizard" })}</Label>
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map((lang) => {
            const active = selectedLanguages.includes(lang);
            return (
              <button key={lang} type="button" onClick={() => toggle(selectedLanguages, setSelectedLanguages, lang)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${active ? "bg-brand-subtle border-brand text-brand-subtle-text" : "bg-card border-border text-foreground hover:bg-muted"}`}>
                {t(`steps.aiConfig.lang.${lang}`, { ns: "wizard" })}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <Button variant="secondary" onClick={onBack}>{t("nav.back", { ns: "wizard" })}</Button>
        <Button className="flex-1" onClick={handleContinue} disabled={saving}>
          {saving ? t("actions.saving", { ns: "common" }) : t("nav.continue", { ns: "wizard" })}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkip}>{t("nav.skip", { ns: "wizard" })}</Button>
      </div>
    </div>
  );
}
